// src/pages/ResetPassword.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  verifyPasswordResetCode,
  confirmPasswordReset
} from "firebase/auth";
import "./ResetPassword.css";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const oobCode = params.get("oobCode");
  const mode = params.get("mode");          // devrait être "resetPassword"
  const continueUrl = params.get("continueUrl"); // si tu l’as défini dans actionCodeSettings

  const [status, setStatus] = useState("checking"); // checking | ready | success | error
  const [targetEmail, setTargetEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");

  // (optionnel) forcer la langue FR pour les éventuels messages internes
  useEffect(() => { auth.languageCode = "fr"; }, []);

  useEffect(() => {
    const check = async () => {
      setStatus("checking");
      setError("");

      if (!oobCode || mode !== "resetPassword") {
        setStatus("error");
        setError("Lien de réinitialisation invalide.");
        return;
      }

      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        setTargetEmail(email || "");
        setStatus("ready");
      } catch (e) {
        console.error("verifyPasswordResetCode error:", e);
        setStatus("error");
        if (e.code === "auth/expired-action-code") setError("Ce lien a expiré.");
        else if (e.code === "auth/invalid-action-code") setError("Lien invalide.");
        else setError("Impossible de valider le lien.");
      }
    };
    check();
  }, [oobCode, mode]);

  const canSubmit = useMemo(() => {
    const okLength = password.length >= 6; // règle minimale Firebase
    const match = password && password === password2;
    return status === "ready" && okLength && match;
  }, [status, password, password2]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!canSubmit) return;
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus("success");
      // Redirige si continueUrl existe, sinon bouton "Retour à la connexion"
      if (continueUrl) {
        window.location.replace(continueUrl);
      }
    } catch (e) {
      console.error("confirmPasswordReset error:", e);
      if (e.code === "auth/weak-password") setError("Mot de passe trop faible (min. 6).");
      else if (e.code === "auth/expired-action-code") setError("Le lien a expiré.");
      else if (e.code === "auth/invalid-action-code") setError("Lien invalide.");
      else setError("Échec de la réinitialisation.");
    }
  };

  if (status === "checking") {
    return (
      <div className="container-reset">
        <div className="card-reset">
          <h2 className="title-reset">Vérification du lien…</h2>
          <p className="muted-reset">Merci de patienter.</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="container-reset">
        <div className="card-reset">
          <h2 className="title-reset">Lien invalide</h2>
          <p className="error-reset">{error}</p>
          <button className="btn-reset" onClick={() => navigate("/")}>
            Revenir à la connexion
          </button>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="container-reset">
        <div className="card-reset">
          <h2 className="title-reset">Mot de passe mis à jour ✅</h2>
          {!continueUrl && (
            <button className="btn-reset" onClick={() => navigate("/")}>
              Se connecter
            </button>
          )}
        </div>
      </div>
    );
  }

  // status === "ready"
  return (
    <div className="container-reset">
      <div className="card-reset">
        <h2 className="title-reset">Réinitialiser le mot de passe</h2>
        {targetEmail && (
          <p className="muted-reset">Compte : <strong>{targetEmail}</strong></p>
        )}

        <form onSubmit={onSubmit} className="form-reset">
          <label className="label-reset">Nouveau mot de passe</label>
          <div className="pwd-row-reset">
            <input
              type={showPwd ? "text" : "password"}
              className="input-reset"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
            <button
              type="button"
              className="toggle-reset"
              onClick={() => setShowPwd((s) => !s)}
              aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showPwd ? "🙈" : "👁️"}
            </button>
          </div>

          <label className="label-reset">Confirmer le mot de passe</label>
          <div className="pwd-row-reset">
          <input
            type={showPwd ? "text" : "password"}
            className="input-reset"
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            placeholder="••••••••"
            required
            minLength={6}
          />
          </div>
          

          {password && password2 && password !== password2 && (
            <p className="error-reset">Les mots de passe ne correspondent pas.</p>
          )}

          {error && <p className="error-reset">{error}</p>}

          <button className="btn-reset" type="submit" disabled={!canSubmit}>
            Mettre à jour mon mot de passe
          </button>
        </form>

        <button className="link-reset" onClick={() => navigate("/")}>
          Revenir à la connexion
        </button>
      </div>
    </div>
  );
}
